import { IPerson, Person } from '@shared/models/person';
import { Company, ICompany } from '@shared/models/company';
import { tryParseDate } from '@shared/functions/try-parse-date';
import { BerthBoatAssignment, IBerthBoatAssignment } from '@shared/models/berth-boat-assignment';
import { InvoicePosition, InvoicePositionDto } from '@shared/models/invoice-position';
import { padLeft } from '@shared/functions';
import { ProductSubscription, ProductSubscriptionDto } from '@shared/models/product-subscription';
import { TenantRelationAssignment, TenantRelationAssignmentDto } from '@shared/models/tenant-relation-assignment';
import { BookingItemType } from '@modules/bcm/@shared/enums/berth-item-type';
import { BcmOrder } from '@shared/models/bcm-order';
import { Boat } from '@shared/models/boat';
import { Berth } from '@shared/models/berth';
import { Contract, IContract } from '@shared/models/contract';
import { BcmDocument, BcmDocumentDto } from '@shared/models/bcm-document';
import { v4 as uuidv4 } from 'uuid';
import { ElectricMeterAssignment, IElectricMeterAssignment } from '@shared/models/electric-meter-assignment';
import { BookingType } from '@modules/bcm/@shared/enums/berth-reservation-type';
import { BcmFinancialRecord, BcmFinancialRecordDto } from '@shared/models/bcm-financial-record';
import { Product } from '@shared/models/product';

export interface IBcmBookingStatus {
    id: string;
    name: string;
}

export class BcmBookingStatus {
    id: string;
    name: string;

    constructor(data: IBcmBookingStatus) {
        this.id = data.id;
        this.name = data.name;
    }
}

export interface IBcmBookingTraveler {
    id?: number;
    person?: IPerson;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    birthDate: Date;
    insertedOn?: Date;
    insertedBy?: string;
    lastUpdateOn?: Date;
    lastUpdateBy?: string;
    uuid?: string;
}

export class BcmBookingTraveler {
    id: number;
    person?: Person;
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    birthDate?: Date;
    insertedOn: Date;
    insertedBy: string;
    lastUpdateOn: Date;
    lastUpdateBy: string;
    uuid: string;

    constructor(dto: IBcmBookingTraveler) {
        this.id = dto.id;
        this.person = dto.person ? new Person(dto.person) : null;
        this.firstName = dto.firstName;
        this.lastName = dto.lastName;
        this.email = dto.email;
        this.phone = dto.phone;
        this.birthDate = dto.birthDate;
        this.insertedOn = dto.insertedOn;
        this.insertedBy = dto.insertedBy;
        this.lastUpdateOn = dto.lastUpdateOn;
        this.lastUpdateBy = dto.lastUpdateBy;
        this.uuid = dto.uuid || uuidv4();
    }
}

export interface BcmBookingGeneralDataDto {
    bookingType?: BookingType;
    reservationText?: string;
    reservedUntil?: Date;
    note?: string;
    status?: IBcmBookingStatus;
    personOrCompany?: 'person' | 'company';
    isBoatOwner?: boolean;
}

// Raw key lending (bcm_person_has_keys) from getBookingById — read only by the
// booking-v2 edit reverse-mapper.
export interface IBcmBookingKeyAssignment {
    id: number;
    bcm_persons_id?: number;
    bcm_boats_id?: number;
    keyNumber?: string;
    keyFeePayedOn?: Date | string;
    keyFeePayedBackOn?: Date | string;
    fromDate?: Date | string;
    toDate?: Date | string;
    note?: string;
    key?: {
        id: number;
        keyName?: string;
        keyType?: string;
        keyId?: string;
        isUnique?: boolean;
        bcm_products_id?: number;
        product?: { id: number; name?: string; price?: number };
    };
    person?: { id: number; fullName?: string; firstName?: string; lastName?: string };
    boat?: { id: number; name?: string };
}

export class BcmBookingGeneralData {
    bookingType?: BookingType;
    reservationText?: string;
    reservedUntil?: Date;
    note?: string;
    status?: BcmBookingStatus;
    personOrCompany?: 'person' | 'company';
    isBoatOwner?: boolean;

    constructor(data: BcmBookingGeneralDataDto) {
        this.bookingType = data.bookingType || BookingType.Booking;
        this.reservationText = data.reservationText || null;
        this.reservedUntil = data.reservedUntil || null;
        this.note = data.note || null;
        this.status = data?.status ? new BcmBookingStatus(data.status) : null;
        this.personOrCompany = data.personOrCompany || null;
        this.isBoatOwner = data.isBoatOwner || false;
    }
}

export enum ParticipantType {
    Person = 'person',
    Company = 'company',
}

export interface ApiClientDeviceOrigin {
    id: number;
    api_clients_id: number;
    bcm_bookings_id: number;
    device_id: string;
    reference_id: string;
}

export interface BookingExternalPaymentProduct {
    id: number;
    product: Product;
    paymentReferenceId: string;
    name: string;
    price: number;
    quantity: number;
    quantityEditable: boolean;
    unit: string;
    taxRate: number;
    totalPrice: number;
}

export interface BookingExternalPayment {
    id: number;
    products: BookingExternalPaymentProduct[];
    paymentReferenceId: string;
    paymentType: string;
    totalPrice: number;
    paymentInfo: string;
}

export interface IBcmBooking {
    id?: number;

    bookingType?: BookingType;
    reservationText?: string;
    reservedUntil?: Date;
    note?: string;
    status?: IBcmBookingStatus;

    personOrCompany?: ParticipantType;
    isBoatOwner?: boolean;

    person: IPerson;
    company: ICompany;

    travelers?: IBcmBookingTraveler[];

    electricMeterAssignments: IElectricMeterAssignment[];

    keyAssignments?: IBcmBookingKeyAssignment[];

    berthAssignments?: IBerthBoatAssignment[];
    positions?: InvoicePositionDto[];
    financialRecords?: BcmFinancialRecordDto[];

    subscriptions?: ProductSubscriptionDto[];

    tenantRelationAssignment?: TenantRelationAssignmentDto;

    contracts?: IContract[];

    documents?: BcmDocumentDto[];

    from?: Date;
    to?: Date;

    apiClientDeviceOrigin?: ApiClientDeviceOrigin;

    externalPaymentData?: BookingExternalPayment;

    bcm_orders_id?: number | null;

    arrivalDone: boolean;
    departureDone: boolean;
    cancelledOn: Date;
    cancellationReason: string;
    insertedOn?: Date;
    insertedBy?: string;
    lastUpdateOn?: Date;
    lastUpdateBy?: string;

    /** List view (bookings page API) */
    bookingNumber?: string;
    clientName?: string;
    boatNames?: string;
    berthLabels?: string;
    tenantRelationName?: string;
    lifecyclePhase?: string;
    statusName?: string;
    totalGrossTurnover?: number | null;
}

export class BcmBooking {

    // values from database
    id: number;

    person: Person;
    company: Company;

    general: BcmBookingGeneralData;

    travelers?: BcmBookingTraveler[];

    electricMeterAssignments: ElectricMeterAssignment[];

    keyAssignments: IBcmBookingKeyAssignment[];

    berthAssignments: BerthBoatAssignment[];
    positions: InvoicePosition[];
    financialRecords: BcmFinancialRecord[];

    subscriptions: ProductSubscription[];

    documents: BcmDocument[];

    tenantRelationAssignment?: TenantRelationAssignment;

    contracts: Contract[];

    arrivalDone: boolean;
    departureDone: boolean;
    cancelledOn: Date;
    cancellationReason: string;

    apiClientDeviceOrigin?: ApiClientDeviceOrigin;
    externalPaymentData?: BookingExternalPayment;

    bcm_orders_id?: number | null;

    insertedOn?: Date;
    insertedBy?: string;
    lastUpdateOn?: Date;
    lastUpdateBy?: string;

    // helper
    number: string; // auto generated booking number by id
    positionsSaldo: number;
    itemType?: BookingItemType;
    openOrders?: BcmOrder[];
    from: Date;
    to: Date;
    boat: Boat;
    arrivalBerth: Berth;
    departureBerth: Berth;
    saldo: number;

    sendConfirmation: boolean;

    bookingNumber?: string;
    clientName?: string;
    boatNames?: string;
    berthLabels?: string;
    tenantRelationName?: string;
    lifecyclePhase?: string;
    statusName?: string;
    totalGrossTurnover?: number | null;
    bookingType?: BookingType;

    constructor(data: IBcmBooking) {
        this.id = data.id;
        this.bcm_orders_id = data.bcm_orders_id ?? null;

        this.berthAssignments = (data.berthAssignments || []).map(assignment => {
            assignment.bookingId = data.id;
            return new BerthBoatAssignment(assignment);
        });

        this.boat = this.berthAssignments.length > 0 ? this.berthAssignments[0].boat : null;

        this.person = data.person ? new Person(data.person) : null;
        this.company = data.company ? new Company(data.company) : null;

        const personIsOwner = this.boat?.owner && this.boat.owner.id === this.person?.id;
        const companyIsOwner = this.boat?.ownerCompany && this.boat.ownerCompany.id === this.company?.id;

        this.general = new BcmBookingGeneralData({
            bookingType: data.bookingType,
            reservationText: data.reservationText,
            reservedUntil: tryParseDate(data.reservedUntil),
            note: data.note,
            status: data.status,
            personOrCompany: data.company ? ParticipantType.Company : ParticipantType.Person,
            isBoatOwner: personIsOwner || companyIsOwner
        });

        this.travelers = (data.travelers || []).map(traveler => new BcmBookingTraveler(traveler));

        this.electricMeterAssignments = (data.electricMeterAssignments || []).map(assignment => new ElectricMeterAssignment(assignment));

        this.keyAssignments = data.keyAssignments || [];

        this.positions = (data.positions || []).map(position => new InvoicePosition(position));

        this.positions.forEach(position => {
            if (position.bcm_berth_has_assignments_id) {
                position.berthAssignmentUuid = this.berthAssignments.find(a => a.id === position.bcm_berth_has_assignments_id)?.uuid;
            }
            if (position.bcm_electric_meter_has_readings_id) {
                position.electricMeterAssignmentUuid = this.electricMeterAssignments.find(a => a.id === position.bcm_electric_meter_has_boats_id)?.uuid;
            }
            return position;
        });

        this.financialRecords = (data.financialRecords || []).map(record => new BcmFinancialRecord(record));

        this.subscriptions = (data.subscriptions || []).map((subscription: ProductSubscriptionDto) => new ProductSubscription(subscription));

        this.tenantRelationAssignment = data.tenantRelationAssignment ? new TenantRelationAssignment(data.tenantRelationAssignment) : null;

        if (this.tenantRelationAssignment) {
            this.tenantRelationAssignment.captureTenantRelation = true;
        }

        this.contracts = (data.contracts || []).map(contract => new Contract(contract));
        this.documents = (data.documents || []).map(doc => new BcmDocument(doc));

        this.arrivalDone = data.arrivalDone;
        this.departureDone = data.departureDone;
        this.cancelledOn = tryParseDate(data.cancelledOn);
        this.cancellationReason = data.cancellationReason;

        this.insertedOn = tryParseDate(data.insertedOn);
        this.insertedBy = data.insertedBy;
        this.lastUpdateOn = tryParseDate(data.lastUpdateOn);
        this.lastUpdateBy = data.lastUpdateBy;

        this.bookingNumber = data.bookingNumber || padLeft(data.id, '000');
        this.clientName = data.clientName;
        this.boatNames = data.boatNames;
        this.berthLabels = data.berthLabels;
        this.tenantRelationName = data.tenantRelationName;
        this.lifecyclePhase = data.lifecyclePhase;
        this.statusName = data.statusName;
        this.totalGrossTurnover = data.totalGrossTurnover ?? null;
        this.bookingType = data.bookingType;

        // helper
        this.number = padLeft(data.id, '000');

        this.positionsSaldo = this.positions
            .map(p => p.price * p.quantity)
            .reduce((acc, val) => acc + val, 0);

        if (data.from) {
            this.from = tryParseDate(data.from);
        } else {
            this.from = this.getMinFrom();
        }

        if (data.to) {
            this.to = tryParseDate(data.to);
        } else {
            this.to = this.getMaxTo();
        }

        this.apiClientDeviceOrigin = data.apiClientDeviceOrigin ?? null;
        this.externalPaymentData = data.externalPaymentData ?? null;

        this.arrivalBerth = this.berthAssignments.length > 0 ? this.berthAssignments[0].berth : null;

        this.departureBerth = this.berthAssignments.length > 0 ? this.berthAssignments[this.berthAssignments.length - 1].berth : null;

        this.saldo = ((this.person || this.company)?.positions || [])
            .reduce((accumulator: number, position) => {
                return accumulator + position.totalPrice;
            }, 0);
    }

    getMinFrom(): Date {
        // from/to einzelner Assignments können null sein (offene/Entwurfs-Buchungen) –
        // null herausfiltern, sonst wirft .getTime() bzw. Math.min liefert NaN.
        const times = this.berthAssignments.map(a => a.from?.getTime()).filter((t): t is number => t != null);
        return times.length > 0 ? new Date(Math.min(...times)) : new Date();
    }

    getMaxTo(): Date {
        const times = this.berthAssignments.map(a => a.to?.getTime()).filter((t): t is number => t != null);
        return times.length > 0 ? new Date(Math.max(...times)) : null;
    }

}
